import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink, navigationMenuTriggerStyle } from '@/components/ui/navigation-menu';
import { Menu as MenuIcon, X as CloseIcon } from 'lucide-react';
import { useAuth } from '../features/auth/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api';
import { RoleType, type User } from '@promo/shared';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';

export function Navbar() {
  const { session, logout } = useAuth();
  const { user, isAuthenticated, isLoading: authLoading } = session;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Role-based access: fetch lists of users by role
  const managersQuery = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.MANAGER],
    queryFn: () => get<User[]>('/users', { role: RoleType.MANAGER }),
  });
  const managers = managersQuery?.data ?? [];
  const loadingManagers = managersQuery?.isLoading ?? false;

  const seniorLeadersQuery = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.SENIOR_LEADER],
    queryFn: () => get<User[]>('/users', { role: RoleType.SENIOR_LEADER }),
  });
  const seniorLeaders = seniorLeadersQuery?.data ?? [];
  const loadingSenior = seniorLeadersQuery?.isLoading ?? false;

  const hrMembersQuery = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.HR],
    queryFn: () => get<User[]>('/users', { role: RoleType.HR }),
  });
  const hrMembers = hrMembersQuery?.data ?? [];
  const loadingHr = hrMembersQuery?.isLoading ?? false;

  const barRaiserQuery = useQuery<User[], Error>({
    queryKey: ['users', 'by-role', RoleType.BAR_RAISER],
    queryFn: () => get<User[]>('/users', { role: RoleType.BAR_RAISER }),
  });
  const barRaisers = barRaiserQuery?.data ?? [];
  const loadingBr = barRaiserQuery?.isLoading ?? false;

  // Auth guard: only show when authenticated and not loading
  if (authLoading || !isAuthenticated || !user) return null;
  const userId = user.id;
  const publicPaths = ['/login', '/verify'];
  if (publicPaths.includes(pathname) || pathname.startsWith('/invitations/')) {
    return null;
  }
  const loadingRoles = loadingManagers || loadingSenior || loadingHr || loadingBr;
  const canViewAdmin = !loadingRoles &&
    [...managers, ...seniorLeaders, ...hrMembers, ...barRaisers].some(u => u.id === userId);

  // Logout handler
  const handleLogout = async () => {
    setMobileOpen(false);
    try {
      await logout();
    } catch {
      // ignore
    }
    navigate('/login', { replace: true });
  };

  return (
    <nav className="sticky top-0 z-50 bg-background border-b">
      <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-4">
        {/* Logo + Preview Badge */}
        <div className="flex items-center gap-2">
          <Link to="/">
            <img src="/tebra-logo.svg" alt="Tebra Logo" className="h-8 w-auto" />
          </Link>
          {(import.meta.env.MODE === "preview" ||
            (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.includes("dev.promo.kareotest.com"))) && (
            <Badge variant="secondary" className="ml-2">
              PREVIEW
            </Badge>
          )}
        </div>

        {/* Desktop Menu */}
        <NavigationMenu className="hidden md:flex">
          <NavigationMenuList>
            {/* Role-based items */}
            {canViewAdmin && (
              <>
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/insights"
                      className={`${navigationMenuTriggerStyle()} nav-underline-animate${pathname === '/insights' ? ' active' : ''}`}
                    >
                      Insights
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/packets"
                      className={`${navigationMenuTriggerStyle()} nav-underline-animate${pathname === '/packets' ? ' active' : ''}`}
                    >
                      Packets
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/management"
                      className={`${navigationMenuTriggerStyle()} nav-underline-animate${pathname === '/management' ? ' active' : ''}`}
                    >
                      Management
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </>
            )}
            {/* Logout always visible */}
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <button onClick={handleLogout} className={navigationMenuTriggerStyle()}>Logout</button>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden p-2"
          onClick={() => setMobileOpen(true)}
        >
          <MenuIcon className="h-6 w-6" />
          <span className="sr-only">Open menu</span>
        </button>
        {/* Overlay */}
        <div
          className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setMobileOpen(false)}
        />
        {/* Sliding Panel */}
        <div
          className={`fixed top-0 right-0 z-50 h-full w-64 bg-background shadow transform transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="flex justify-end p-4">
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2"
            >
              <CloseIcon className="h-6 w-6" />
              <span className="sr-only">Close menu</span>
            </button>
          </div>
          <nav className="flex flex-col space-y-4 p-4">
            {canViewAdmin && (
              <>
                <Link to="/insights" className={`nav-underline-animate text-lg${pathname === '/insights' ? ' active' : ''}`} onClick={() => setMobileOpen(false)}>Insights</Link>
                <Link to="/packets" className={`nav-underline-animate text-lg${pathname === '/packets' ? ' active' : ''}`} onClick={() => setMobileOpen(false)}>Packets</Link>
                <Link to="/management" className={`nav-underline-animate text-lg${pathname === '/management' ? ' active' : ''}`} onClick={() => setMobileOpen(false)}>Management</Link>
              </>
            )}
            <button onClick={handleLogout} className="text-lg text-left">Logout</button>
          </nav>
        </div>
      </div>
    </nav>
  );
} 